// @flow
import { getLogger } from "@cld/upload-widget";
import appConfig from "appConfig";
import { getCamera, createElement } from "utils/domUtils";

const logger = getLogger();

type optionsType = { videoElm: HTMLVideoElement, canvasElm: HTMLCanvasElement };

type MediaStream = { stop: Function, getTracks: Function };

const cameraConstraints = { video: { width: { ideal: 1280, max: 1920 } }, audio: false };

const renderToCanvas = (options, canvas: HTMLCanvasElement, fitToScreen: boolean = false) => {
	const { videoElm } = options;
	const { videoWidth, videoHeight } = videoElm;

	const canvasHeight = fitToScreen ? Math.min(videoHeight, appConfig.ui.cameraPreviewHeight) : videoHeight;
	const ratio = canvasHeight / videoHeight;

	canvas.height = canvasHeight;
	canvas.width = videoWidth * ratio;

	logger.log(`[cameraService]: rendering scaled canvas for camera preview. width: ${canvas.width}, height: ${canvas.height}`);
	// scale the camera video snapshot into the canvas
	canvas.getContext("2d").drawImage(videoElm, 0, 0, videoWidth, videoHeight, 0, 0, canvas.width, canvas.height);
};

export default (options: optionsType) => getCamera(cameraConstraints)
	.then((mediaStream: ?MediaStream) => {
		logger.log("[cameraService]: user approved access to camera device");

		if (self.navigator.mozGetUserMedia && mediaStream) {
			// $FlowFixMe - flow doesnt know mozSrcObject
			options.videoElm.mozSrcObject = mediaStream;
			options.videoElm.srcObject = mediaStream;
		} else {
			try {
				options.videoElm.srcObject = mediaStream;
			} catch (e) {
				const vendorURL = (window.URL || window.webkitURL);
				options.videoElm.setAttribute("src", vendorURL.createObjectURL(mediaStream));
			}
		}

		options.videoElm.play();

		const snapPreview = () => renderToCanvas(options, options.canvasElm, true);

		const stop = () => {
			logger.log("[cameraService]: about to stop camera video stream");

			try {
				if (mediaStream) {
					if (mediaStream.stop) {
						mediaStream.stop();
					} else {
						mediaStream.getTracks()
							.forEach((t) => t.stop());
					}
				}
			} catch (err) {
				logger.warn("[cameraService]: encountered error when stopping video stream", err);
			}

			mediaStream = null;
		};

		const snapPhoto = () => {
			const canvas: HTMLCanvasElement = createElement("canvas");

			renderToCanvas(options, canvas); // get a canvas that isnt scaled for the UI, to get the full res from the camera

			return canvas.toDataURL("image/jpeg");
		};

		return {
			snapPreview,
			snapPhoto,
			stop,
		};
	})
	.catch((error) => {
		logger.log("[cameraService]: received error when getting user camera media", error);
		return Promise.reject(error);
	});
